/**
 * WHICH AGENTS ARE RESTARTING RIGHT NOW.
 *
 * Owner, 2026-09-26, after watching a restart from inside the session: 假設使用者在
 * Session 裡面的時候, 他如果按下 Restart 的這個按鍵, 需要先退出 Session, 然後讓 Session 等
 * 一陣子, 等 Session 真正 restart 成功之後呢, User 才可以再次自動回到 Session 裡面, 或者是
 * 要有一個等待準備的一個蓋版轉場的畫面, 讓使用者可以知道, 哦, 現在 Session 還不是能夠繼續看
 * 的一個狀態.
 *
 * The problem is real and he saw it: the pane is killed out from under a terminal that
 * is still attached to it, so the person watches their session dissolve into whatever
 * the socket does next — which on his screen was a bare shell prompt. Nothing on screen
 * said "this is expected and it is coming back".
 *
 * A COVER, NOT AN EXIT, of the two shapes he offered. Leaving the session and returning
 * to it means rebuilding where the person was — scroll position, split state, which pane
 * was focused — and getting any of that wrong is a worse outcome than the problem. A
 * cover keeps their place and only hides a surface that is, for those few seconds,
 * genuinely not showing anything true.
 *
 * WHY A MODULE AND NOT PROPS. The button lives in the agent's card and the surface that
 * must react is the terminal, and the two have no common parent that already knows about
 * restarts — App renders AgentCard from one branch and AgentPanel from another. Threading
 * a flag down both would put a restart-shaped hole in every component in between. This is
 * the same shape as push/pushNav.ts's opener registry, for the same reason.
 *
 * KEYED BY SESSION NAME, not by hash, because that is what every surface here already
 * holds and what the edge route takes.
 */

type Listener = () => void;

const restarting = new Set<string>();
const listeners = new Set<Listener>();

function announce(): void {
  for (const fn of [...listeners]) fn();
}

/** The button is pressed. Cover the surface before the pane can die under it. */
export function beginRestart(session: string): void {
  if (!session || restarting.has(session)) return;
  restarting.add(session);
  announce();
}

/**
 * The machine has answered — either the agent is back, or it refused and never stopped.
 *
 * BOTH OUTCOMES END THE COVER, and that is deliberate. A refusal means the agent was
 * never touched, so the terminal underneath is still live and correct; leaving a
 * "restarting…" cover over a working session would be a lie that the person cannot
 * dismiss.
 */
export function endRestart(session: string, outcome: RestartOutcome = "refused"): void {
  if (!restarting.has(session)) return;
  outcomes.set(session, outcome);
  restarting.delete(session);
  announce();
}

/**
 * HOW THE LAST RESTART OF THIS SESSION ENDED — owner, 2026-09-26: 在切換 Agent 模型的時候
 * 你那個 Terminal 在還沒準備好的時候, 你應該要用一個轉場的狀態, 讓使用者不會看到空白的
 * empty Terminal.
 *
 * "restarted" answers the moment the new process EXISTS, which is seconds before the CLI
 * has drawn anything — so lifting the cover on that answer showed an empty terminal. The
 * terminal keeps the cover up after an "ok" until its screen actually shows the agent
 * (TerminalView). A refusal lifts it at once: the agent was never stopped, so what is
 * underneath is live and correct. Defaults to "refused" so a caller that does not say
 * can never leave a cover waiting for a paint that is not coming.
 */
export type RestartOutcome = "ok" | "refused";
const outcomes = new Map<string, RestartOutcome>();
export function restartOutcome(session: string | null | undefined): RestartOutcome | null {
  return session ? (outcomes.get(session) ?? null) : null;
}

export function isRestarting(session: string | null | undefined): boolean {
  return !!session && restarting.has(session);
}

export function onRestartingChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Tests only — the set is module state and would otherwise leak between them. */
export function resetRestartingForTest(): void {
  restarting.clear();
  listeners.clear();
  outcomes.clear();
}
